import { useFormik } from 'formik';
import { registerValidation } from './validations';
import { ICustomersRegisterRequest } from '@/shared/endpoints/customers/models/request';
import { useCallback, useMemo } from 'react';
import { phoneInput } from '@/shared/helpers/phone-input';
import { getDefaultOnboarding } from '../helpers';
import { useHeaderHeight } from '@/shared/components/header-height-context';

// Components
import Box from '@mui/material/Box';
import { Alert, Checkbox, Input, Select } from '@/shared/ui';
import Button from '@/shared/ui/button';
import { regexAndErrorMessageMapping } from '../../pages/login/reset-password/confirm-password';

const validationSchema = registerValidation;

const CaregiverRelationToPatientOption = [
    { label: 'Parent', value: 'PARENT' },
    { label: 'Legal Guardian', value: 'LEGAL_GUARDIAN' },
    { label: 'Spouse', value: 'SPOUSE' },
    { label: 'Caregiver', value: 'CAREGIVER' },
    { label: 'Other', value: 'OTHER' }
];

interface IRegisterFormData {
    email: string;
    phoneNumber: string;
    password: string;
    inviteACaregiver: boolean;
    terms: boolean;
    relationship: string;
    caregiverFirstName: string;
    caregiverLastName: string;
    caregiverEmail: string;
    caregiverPhone: string;
}

interface IProps {
    onSubmit: (data: ICustomersRegisterRequest) => void;
    isLoading?: boolean;
}

const fields = [
    { name: 'email', queryClassName: 'register__email' },
    { name: 'phoneNumber', queryClassName: 'register__phone' },
    { name: 'password', queryClassName: 'register__password' },
    { name: 'inviteACaregiver', queryClassName: 'login__terms' },
    { name: 'relationship', queryClassName: 'register__relationship' },
    { name: 'caregiverFirstName', queryClassName: 'register__caregiver_first_name' },
    { name: 'caregiverLastName', queryClassName: 'register__caregiver_last_name' },
    { name: 'caregiverEmail', queryClassName: 'register__caregiver_email' },
    { name: 'caregiverPhone', queryClassName: 'register__caregiver_phone' }
];

const RegisterForm = ({ onSubmit, isLoading }: IProps) => {
    const headerHeight = useHeaderHeight();
    const initialValues: IRegisterFormData = useMemo(
        () => ({
            email: '',
            phoneNumber: '',
            password: '',
            inviteACaregiver: false,
            terms: true,
            relationship: '',
            caregiverFirstName: '',
            caregiverLastName: '',
            caregiverEmail: '',
            caregiverPhone: '',
        }),
        []
    );

    const modifyPayload = useCallback((data: IRegisterFormData) => {
        const payload: ICustomersRegisterRequest = {
            terms: false,
            email: '',
            password: '',
            userOnboarding: getDefaultOnboarding(data.phoneNumber),
            linkedUsers: [
                {
                    email: '',
                    userProfile: {
                        relationship: '',
                        firstName: '',
                        lastName: '',
                        communication: {
                            Communication: [
                                {
                                    Number: '',
                                    Qualifier: 'CP',
                                    Extension: ''
                                }
                            ]
                        }
                    }
                }
            ]
        };

        payload.terms = data.terms;
        payload.email = data.email;
        payload.password = data.password;
        payload.userOnboarding.details.phoneNumber = data.phoneNumber.replaceAll('-', '');

        if (data.inviteACaregiver && payload.linkedUsers) {
            payload.linkedUsers[0].email = data.caregiverEmail;
            payload.linkedUsers[0].userProfile.relationship = data.relationship;
            payload.linkedUsers[0].userProfile.firstName = data.caregiverFirstName;
            payload.linkedUsers[0].userProfile.lastName = data.caregiverLastName;
            payload.linkedUsers[0].userProfile.communication.Communication[0].Number = data.caregiverPhone.replaceAll('-', '');
        } else delete payload.linkedUsers;
        return payload;
    }, []);

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,
        onSubmit: values => {
            onSubmit(modifyPayload(values));
        }
    });

    const scrollToInvalidField = useCallback(async () => {
        const errors = await formik.validateForm();
        if (!Object.keys(errors).length) return;

        const errorFields = { ...errors };
        const fieldWithError = fields.find(field => Object.keys(errorFields).includes(field.name));
        if (fieldWithError) {
            const element = document.querySelector(`.${fieldWithError.queryClassName}`);
            if (element) {
                const elementPosition = element.getBoundingClientRect().top + window.scrollY - headerHeight;
                setTimeout(() => {
                    window.scrollTo({
                        top: elementPosition,
                        behavior: 'smooth'
                    });
                }, 200)
            }
        }
    }, [formik]);

    const validationRules = useMemo(() => {
        const rules: string[] = [];
        regexAndErrorMessageMapping.forEach(item => {
            const isValid = item.regex.test(formik.values.password);
            if (!isValid) {
                rules.push(item.errorMessage);
            }
        });
        return rules;
    }, [formik.values.password]);

    return (
        <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px'
            }}
            data-testid="register-form-container"
        >
            <Input
                className="register__email"
                label="Email"
                name="email"
                type="text"
                placeholder="ex. janed@gmail.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.email)}
                errorMessage={formik.errors.email}
                required
                disabled={isLoading}
                testId="register-form-email"
            />

            <Input
                className="register__phone"
                label="Phone Number"
                name="phoneNumber"
                type="text"
                placeholder="ex. 945-233-1234"
                value={phoneInput(formik.values.phoneNumber)}
                onChange={event => formik.setFieldValue('phoneNumber', phoneInput(event))}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.phoneNumber)}
                errorMessage={formik.errors.phoneNumber}
                required
                disabled={isLoading}
                testId="register-form-phone"
            />

            <Input
                className="register__password"
                label="Create Password"
                name="password"
                type="password"
                placeholder="•••••••••"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors.password)}
                required
                disabled={isLoading}
                testId="register-form-create-password"
            />

            {formik.errors.password && !!validationRules.length && (
                <Alert testId="register-form-password-validation-rules" label="Password must contain:" labelClassName="font-dmSans">
                    <ul data-testid="register-form-password-validation-rules-content" className="list-disc pl-4 text-sm lg:text-lg">
                        {validationRules.map((e, index) => (
                            <li data-testid={`register-form-password-validation-rule-${index}`} key={index} className="font-dmSans text-base font-semibold lg:text-lg">
                                {e}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <Checkbox
                testId="register-form-invite-caregiver"
                uncheckedStrokeColor='#9FA4A1'
                className={`!items-center !gap-5 login__terms border p-5 rounded-2xl ${formik.values.inviteACaregiver ? 'border-primary-400' : 'border-gray-300'
                    }`}
                label={
                    <div data-testid="register-form-invite-caregiver-text" className="flex flex-col">
                        <span style={{ fontWeight: '700' }} className="font-dmSansBold lg:text-lg">Invite a Caregiver</span>
                        <span className="font-dmSans text-base lg:text-lg">
                            Share account access with a family member, friend, or health professional.
                        </span>
                    </div>
                }
                disabled={isLoading}
                name="inviteACaregiver"
                checked={formik.values.inviteACaregiver}
                onChange={formik.handleChange}
            />
            {formik.values.inviteACaregiver && (
                <>
                    <Select
                        className="register__relationship"
                        label="Relationship to Patient"
                        name="relationship"
                        placeholder="Select"
                        value={formik.values.relationship}
                        options={CaregiverRelationToPatientOption}
                        onChange={value => formik.setFieldValue('relationship', value)}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.relationship)}
                        errorMessage={formik.errors.relationship}
                        required={formik.values.inviteACaregiver}
                        disabled={isLoading}
                        testId="register-form-relationship-to-patient"
                    />

                    <Input
                        className="register__caregiver_first_name"
                        label="Caregiver First Name"
                        name="caregiverFirstName"
                        type="text"
                        value={formik.values.caregiverFirstName}
                        placeholder="ex. Jane"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.caregiverFirstName)}
                        errorMessage={formik.errors.caregiverFirstName}
                        required={formik.values.inviteACaregiver}
                        disabled={isLoading}
                        testId="register-form-caregive-first-name"
                    />

                    <Input
                        className="register__caregiver_last_name"
                        label="Caregiver Last Name"
                        name="caregiverLastName"
                        type="text"
                        value={formik.values.caregiverLastName}
                        placeholder="ex. Doh"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.caregiverLastName)}
                        errorMessage={formik.errors.caregiverLastName}
                        required={formik.values.inviteACaregiver}
                        disabled={isLoading}
                        testId="register-form-caregive-last-name"
                    />

                    <Input
                        className="register__caregiver_email"
                        label="Caregiver Email"
                        name="caregiverEmail"
                        type="text"
                        placeholder="ex. janed@gmail.com"
                        value={formik.values.caregiverEmail}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.caregiverEmail)}
                        errorMessage={formik.errors.caregiverEmail}
                        required={formik.values.inviteACaregiver}
                        disabled={isLoading}
                        testId="register-form-caregive-email"
                    />

                    <Input
                        className="register__caregiver_phone"
                        label="Caregiver Phone Number"
                        name="caregiverPhone"
                        type="text"
                        placeholder="ex. 945-233-1234"
                        value={phoneInput(formik.values.caregiverPhone)}
                        onChange={event => formik.setFieldValue('caregiverPhone', phoneInput(event))}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.caregiverPhone)}
                        errorMessage={formik.errors.caregiverPhone}
                        required={formik.values.inviteACaregiver}
                        disabled={isLoading}
                        testId="register-form-caregive-phone"
                    />
                </>
            )}

            <Checkbox
                testId="register-form-cookie-session"
                uncheckedStrokeColor='#9FA4A1'
                className={`!items-center !gap-5 login__terms border p-5 rounded-2xl ${formik.values.terms ? 'border-primary-400' : 'border-gray-300'
                    }`}
                label={
                    <span className="font-dmSans text-base xl:text-lg">
                        Your session may be recorded to improve user experience. Uncheck to Opt out.{' '}
                        <a
                            target="_blank"
                            className="cursor-pointer font-dmSansBold underline"
                            href="https://medbox.com/cookie-policy/"
                            style={{ fontWeight: '700' }}
                        >
                            Cookie Policy
                        </a>
                    </span>
                }
                disabled={false}
                name="terms"
                checked={formik.values.terms}
                onChange={formik.handleChange}
            />

            <Button
                className="register__submit"
                loading={isLoading}
                type="submit"
                variant="contained"
                color="primary"
                showEndIcon
                sx={{
                    boxShadow: '0px 4px 7.1px 0px #0000000F'
                }}
                onClick={scrollToInvalidField}
                testId="button-register-create-account"
            >
                Create Account
            </Button>
        </Box>
    );
};

export default RegisterForm;
